import React, { useState, useRef, useEffect } from "react";

const THEME_COLOR = "#2EBAA3";

const ThemeSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  placeholderClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div className={`relative inline-block ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="min-w-[140px] w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md outline-none transition-all duration-200 hover:border-[#2EBAA3]/50 focus:border-[#2EBAA3] focus:ring-2 focus:ring-[#2EBAA3]/20 cursor-pointer text-left flex items-center justify-between gap-2"
        style={{ borderColor: open ? THEME_COLOR : undefined }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={displayLabel}
      >
        <span className={!selectedOption ? placeholderClassName || "text-gray-500" : ""}>
          {displayLabel}
        </span>
        <svg
          className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke={THEME_COLOR}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <ul
          className="absolute z-50 mt-1 w-full min-w-[140px] py-1 bg-white border rounded-md shadow-lg border-gray-200 max-h-60 overflow-auto"
          role="listbox"
          style={{ borderColor: THEME_COLOR }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(opt.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(opt.value);
                  }
                }}
                tabIndex={0}
                className={`px-3 py-2.5 text-sm cursor-pointer transition-colors duration-150 ${
                  isSelected
                    ? "font-semibold text-white"
                    : "text-gray-700 hover:bg-[#2EBAA3]/10 hover:text-gray-900"
                }`}
                style={{
                  backgroundColor: isSelected ? THEME_COLOR : undefined,
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ThemeSelect;
